import { create } from "zustand";
import { persist } from "zustand/middleware";
import { diffOperationsToHtml, getProcessedDiffResults } from "../utils/diff-utils";
import { lcsDiff } from "../utils/diff-sentence/diff-sentence";
import { Editor } from "@tiptap/react";
import { createEditorExtensionsWithDiff } from "../widgets/TipTapEditor/utils/editor-extensions";
import { LS_DIFF_STORAGE } from "../shared/local-storage/keys";
import { convertMarkdownLinksToHtml } from "../utils/markdown";

type TDiffKey = `${string}:${string}`;

type TDiffEditorState = {
  htmlContent: string;
  timestamp?: string;
} | null;

type TDiffEditorStates = {
  [key: TDiffKey]: TDiffEditorState;
};

interface DiffState {
  editorStates: TDiffEditorStates;

  getDiffEditor: (projectId: string, chapterId: string) => TDiffEditorState;
  saveDiffEditor: (projectId: string, chapterId: string, htmlContent: string) => void;
  clearDiffEditor: (projectId: string, chapterId: string) => void;

  createPendingChanges: (projectId: string, chapterId: string, newContent: string, oldContent: string) => void;
  getAcceptedVersion: (projectId: string, chapterId: string) => string | null;
  acceptChanges: (projectId: string, chapterId: string, setContent: (content: string) => void) => void;
  rejectChanges: (projectId: string, chapterId: string, setContent: (content: string) => void) => void;
}

const createDiffKey = (projectId: string, chapterId: string): TDiffKey => {
  return `${projectId}:${chapterId}`;
};

export const useDiffStore = create<DiffState>()(
  persist(
    (set, get) => ({
      editorStates: {},

      getDiffEditor: (projectId: string, chapterId: string) => {
        const key = createDiffKey(projectId, chapterId);
        return get().editorStates[key] || null;
      },

      saveDiffEditor: (projectId: string, chapterId: string, htmlContent: string) => {
        const key = createDiffKey(projectId, chapterId);
        set((state) => ({
          editorStates: {
            ...state.editorStates,
            [key]: {
              htmlContent,
              timestamp: new Date().toISOString()
            }
          }
        }));
      },

      clearDiffEditor: (projectId: string, chapterId: string) => {
        const key = createDiffKey(projectId, chapterId);
        set((state) => ({
          editorStates: {
            ...state.editorStates,
            [key]: null
          }
        }));
      },

      createPendingChanges: (projectId: string, chapterId: string, newContent: string, oldContent: string) => {
        // not the best place to do it, but the idea is that every time we get something out ot tiptap
        // we don't want to habe markdown links there
        // At this point I spent too much time on fixing links so may refactor this call later.
        const newC = newContent;
        const oldC = convertMarkdownLinksToHtml(oldContent);
        const diff = lcsDiff(oldC, newC);
        const diffs = getProcessedDiffResults(diff);
        if (diffs.some((diff) => diff.type !== "equal")) {
          const html = diffOperationsToHtml(diffs);
          get().saveDiffEditor(projectId, chapterId, html);
        }
      },

      getAcceptedVersion: (projectId: string, chapterId: string) => {
        const editorState = get().getDiffEditor(projectId, chapterId);
        if (!editorState) return null;

        const tempEditor = new Editor({
          extensions: createEditorExtensionsWithDiff(),
          content: editorState.htmlContent
        });

        let hasMoreDiffs = true;
        while (hasMoreDiffs) {
          const operations: Array<{ pos: number; nodeSize: number; diffType: string; nodeType: string }> = [];

          tempEditor.state.doc.descendants((node, pos) => {
            if (node.type.name === "diffInline" || node.type.name === "diffBlock") {
              operations.push({
                pos,
                nodeSize: node.nodeSize,
                diffType: node.attrs.diffType,
                nodeType: node.type.name
              });
            }
            return true;
          });

          if (operations.length === 0) {
            hasMoreDiffs = false;
          } else {
            operations.reverse().forEach(({ pos, nodeSize, diffType, nodeType }) => {
              try {
                if (nodeType === "diffBlock") {
                  tempEditor.commands.acceptDiffBlock({ pos, nodeSize, diffType });
                } else if (nodeType === "diffInline") {
                  tempEditor.commands.acceptDiffInline({ pos, nodeSize, diffType });
                }
              } catch (error) {
                console.error("Error processing diff operation:", error);
              }
            });
          }
        }

        const finalMarkdown = tempEditor.storage.markdown.getMarkdown();
        tempEditor.destroy();

        return finalMarkdown;
      },

      acceptChanges: (projectId: string, chapterId: string, setContent: (content: string) => void) => {
        const finalMarkdown = get().getAcceptedVersion(projectId, chapterId);
        if (!finalMarkdown) return;

        setContent(finalMarkdown);
        get().clearDiffEditor(projectId, chapterId);
      },

      rejectChanges: (projectId: string, chapterId: string, setContent: (content: string) => void) => {
        const editorState = get().getDiffEditor(projectId, chapterId);
        if (!editorState) return;

        const tempEditor = new Editor({
          extensions: createEditorExtensionsWithDiff(),
          content: editorState.htmlContent
        });

        let hasMoreDiffs = true;
        while (hasMoreDiffs) {
          const operations: Array<{ pos: number; nodeSize: number; diffType: string; nodeType: string }> = [];

          tempEditor.state.doc.descendants((node, pos) => {
            if (node.type.name === "diffInline" || node.type.name === "diffBlock") {
              operations.push({
                pos,
                nodeSize: node.nodeSize,
                diffType: node.attrs.diffType,
                nodeType: node.type.name
              });
            }
            return true;
          });

          if (operations.length === 0) {
            hasMoreDiffs = false;
          } else {
            operations.reverse().forEach(({ pos, nodeSize, diffType, nodeType }) => {
              try {
                if (nodeType === "diffBlock") {
                  tempEditor.commands.rejectDiffBlock({ pos, nodeSize, diffType });
                } else if (nodeType === "diffInline") {
                  tempEditor.commands.rejectDiffInline({ pos, nodeSize, diffType });
                }
              } catch (error) {
                console.error("Error processing diff operation:", error);
              }
            });
          }
        }

        const finalMarkdown = tempEditor.storage.markdown.getMarkdown();
        tempEditor.destroy();

        setContent(finalMarkdown);
        get().clearDiffEditor(projectId, chapterId);
      }
    }),
    {
      name: LS_DIFF_STORAGE,
      skipHydration: true,
      partialize: (state) => ({
        editorStates: state.editorStates
      })
    }
  )
);
